import { REPORT_TYPE, SECTION_TYPE } from "@/app/report/common/constants"
import { StatsSettings } from "@/app/report/common/type"
import { getGroupColumns } from "@/app/report/common/customTableColumns"
import type { ReportSortSetting } from "@/stores/reportSort"
import { ReportTemplate, Section } from "@/types/report"
import {
  buildPersistedSortKey,
  loadCustomTableSort,
  saveCustomTableSort,
} from "@/utils/reportSortStorage"

/**
 * 폐지 예정인 Stats 섹션(stats / playerStats / activityStats)을 Custom Table 섹션으로 바꾸는 런타임 대치 (일감 #99 2단계).
 *
 * 저장된 템플릿은 건드리지 않고 "읽을 때" 바꾼다 — 리포트 화면·PDF·설정 화면·일괄 CSV 가 모두
 * useReportTemplateDetailQuery 를 거치므로 그 한 곳에서 호출한다. 설정 화면에서 저장하면 변환된 customTable 로 서버에 기록된다.
 * 안내 노출은 하지 않는다(결정 2026-09-08).
 *
 * 대응: stats(Daily) → player 그룹 / playerStats(Trend) → player 그룹 / activityStats(Trend) → activity 그룹.
 */
export const LEGACY_STATS_SECTION_TYPES: string[] = [
  SECTION_TYPE.STATS,
  SECTION_TYPE.PLAYER_STATS,
  SECTION_TYPE.ACTIVITY_STATS,
]

// Stats 의 고정 컬럼·정렬 키 → Custom Table 컬럼 키
const LEGACY_COLUMN_KEY: Record<string, string> = {
  playerName: "nickname",
  jersey: "uniformNumber",
  activity: "activityCount",
  date: "activityStartDate",
  matchDayType: "matchDay",
}

const mapLegacyColumnKey = (key: string): string => LEGACY_COLUMN_KEY[key] ?? key

export function isLegacyStatsSection(sectionType: string): boolean {
  return LEGACY_STATS_SECTION_TYPES.includes(sectionType)
}

export function legacyStatsGroupKey(sectionType: string): "player" | "activity" {
  return sectionType === SECTION_TYPE.ACTIVITY_STATS ? "activity" : "player"
}

/** 템플릿 type 이 비어 있으면 섹션 종류로 리포트 종류를 추정한다 (stats 만 Daily) */
function resolveReportType(template: Pick<ReportTemplate, "type" | "content">): REPORT_TYPE {
  if (template.type === REPORT_TYPE.DAILY || template.type === REPORT_TYPE.TREND) {
    return template.type
  }

  return template.content.some((section) => section.section === SECTION_TYPE.STATS)
    ? REPORT_TYPE.DAILY
    : REPORT_TYPE.TREND
}

export function convertLegacyStatsSettings(
  sectionType: string,
  settings: StatsSettings,
  reportType: REPORT_TYPE,
): StatsSettings {
  const groupKey1 = legacyStatsGroupKey(sectionType)
  const availableColumns = getGroupColumns(groupKey1, reportType)
  const fixedColumns = (settings.fixedColumns ?? [])
    .map(mapLegacyColumnKey)
    .filter((key, index, list) => availableColumns.includes(key) && list.indexOf(key) === index)

  return {
    title: settings.title,
    // Daily Stats 에는 calcType 이 없다 — 서버 CustomTableSetting 은 calcType 을 요구하므로 채운다
    calcType: settings.calcType || "average",
    metrics: [...(settings.metrics ?? [])],
    fixedColumns,
    groupKey1,
    groupKey2: null,
    // 기존 Stats 는 Total/Average 행을 항상 보여줬으므로 대치된 표도 둘 다 켠다 (#155)
    summaryRows: { total: true, average: true },
  }
}

/** Stats 템플릿의 정렬 설정 → Custom Table 클라이언트 정렬(키만 바꿔 준다) */
export function convertLegacySortSetting(settings: StatsSettings): ReportSortSetting | undefined {
  const sort = settings.sortSetting
  if (!sort?.key || !sort.order) {
    return undefined
  }
  const order = String(sort.order).toLowerCase()
  if (order !== "asc" && order !== "desc") {
    return undefined
  }

  return { key: mapLegacyColumnKey(sort.key), order }
}

// 변환된 설정 객체를 기억해 둔다 — 저장 데이터에 표식을 넣으면 서버 DTO 검증(미지 필드 400)에 걸리므로 메모리에서만 구분한다.
// PDF 빌더의 Table 은 대치된 Stats 표가 아니라 진짜 Custom Table 을 따른다 (2026-09-30 요청)
const CONVERTED_LEGACY_SETTINGS = new WeakSet<object>()

/** 이 Custom Table 설정이 옛 Stats(Stats by Player 등)에서 읽을 때 변환된 것인지 */
export function isConvertedLegacyStats(settings: object | null | undefined): boolean {
  return !!settings && CONVERTED_LEGACY_SETTINGS.has(settings)
}

export function convertLegacySection(section: Section, reportType: REPORT_TYPE): Section {
  if (!isLegacyStatsSection(section.section) || Array.isArray(section.settings)) {
    return section
  }

  const settings = convertLegacyStatsSettings(
    section.section,
    section.settings as StatsSettings,
    reportType,
  )
  CONVERTED_LEGACY_SETTINGS.add(settings)

  return {
    ...section,
    section: SECTION_TYPE.CUSTOM_TABLE,
    settings,
  }
}

/** 템플릿에 Stats 섹션이 없으면 같은 객체를 그대로 돌려준다(참조 안정) */
export function convertLegacyReportTemplate<T extends Pick<ReportTemplate, "type" | "content">>(
  template: T,
): T {
  if (!template?.content?.some((section) => isLegacyStatsSection(section.section))) {
    return template
  }
  const reportType = resolveReportType(template)

  return {
    ...template,
    content: template.content.map((section) => convertLegacySection(section, reportType)),
  }
}

/**
 * Stats 템플릿에 저장돼 있던 정렬을 Custom Table 의 클라이언트 정렬 저장소에 한 번 옮겨 준다.
 * 이미 사용자가 정렬을 바꿔 저장해 둔 섹션은 덮어쓰지 않는다. 변환 전 원본 템플릿으로 호출한다.
 */
export function seedLegacyStatsSort(template: ReportTemplate): void {
  if (!template?.content?.some((section) => isLegacyStatsSection(section.section))) {
    return
  }
  const reportType = resolveReportType(template)

  template.content.forEach((section, index) => {
    if (!isLegacyStatsSection(section.section) || Array.isArray(section.settings)) {
      return
    }
    const sort = convertLegacySortSetting(section.settings as StatsSettings)
    if (!sort) {
      return
    }
    const key = buildPersistedSortKey(reportType, template.idfTemplate, index)
    if (!loadCustomTableSort(key)) {
      saveCustomTableSort(key, sort)
    }
  })
}
